import { Download, Search, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AppCard from "../../components/AppCard";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import { useAuth } from "../../providers/AuthProvider";
import { useDemo } from "../../providers/DemoProvider";
import { formatDateTime } from "../../services/demoSelectors";
import { downloadCsv } from "../../utils/downloadCsv";

export default () => {
  const { session } = useAuth();
  const { state, deleteWaitlist, deleteUser, toggleUserStatus } = useDemo();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"waitlist" | "users" | "audit">("waitlist");
  const waitlistSearch = useForm<{ q: string }>({ defaultValues: { q: "" } });
  const usersSearch = useForm<{ q: string }>({ defaultValues: { q: "" } });
  const auditSearch = useForm<{ q: string }>({ defaultValues: { q: "" } });

  if (session?.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8 text-slate-900">
        <div className="max-w-md space-y-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-600 shadow-sm"><ShieldCheck className="h-8 w-8" /></div>
          <h1 className="text-2xl font-semibold tracking-tight">Unauthorized Access</h1>
          <p className="font-medium leading-relaxed text-slate-400">System privileges are required to access the administrative portal. Attempt recorded.</p>
          <Button onClick={() => navigate("/dashboard")} className="px-8 text-xs font-semibold">Return to Secure Gateway</Button>
        </div>
      </div>
    );
  }

  const waitQ = waitlistSearch.watch("q").toLowerCase();
  const userQ = usersSearch.watch("q").toLowerCase();
  const auditQ = auditSearch.watch("q").toLowerCase();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-indigo-100">
      <div className="mx-auto max-w-7xl p-8 md:p-12">
        <header className="mb-10 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400"><ShieldCheck className="h-3.5 w-3.5 text-blue-600" /> Administrative Infrastructure</div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">Control Panel</h1>
          </div>
          <Button variant="outline" className="bg-white text-xs font-semibold" onClick={() => navigate("/dashboard")}>Back to Operations</Button>
        </header>

        <div className="mb-8 flex w-fit gap-1 rounded-2xl bg-slate-100 p-1">
          <button onClick={() => setTab("waitlist")} className={`rounded-xl px-6 py-2.5 text-xs font-semibold transition-all ${tab === "waitlist" ? "bg-white text-blue-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>Onboarding Queue</button>
          <button onClick={() => setTab("users")} className={`rounded-xl px-6 py-2.5 text-xs font-semibold transition-all ${tab === "users" ? "bg-white text-blue-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>Identity Vault</button>
          <button onClick={() => setTab("audit")} className={`rounded-xl px-6 py-2.5 text-xs font-semibold transition-all ${tab === "audit" ? "bg-white text-blue-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>Audit Log</button>
        </div>

        {tab === "waitlist" && (
          <AppCard title="Queue" subtitle="Managed prospects awaiting provision" action={<Button variant="outline" size="sm" className="text-xs font-semibold" onClick={() => downloadCsv("waitlist.csv", [["Name", "Email", "Phone", "Date"], ...state.waitlistEntries.map(entry => [entry.name, entry.email, entry.phone, entry.createdAt])])}><Download className="mr-2 h-3.5 w-3.5" /> Export DB</Button>}>
            <div className="space-y-4">
              <div className="relative">
                <input {...waitlistSearch.register("q")} placeholder="Filter identities..." className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-10 text-sm outline-none focus:border-blue-500" />
                <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-300" />
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-400"><tr><th className="px-6 py-4">Identity</th><th className="px-6 py-4">Status</th><th className="px-6 py-4">Registered</th><th className="px-6 py-4 text-right">Action</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {state.waitlistEntries.filter(entry => `${entry.name} ${entry.email} ${entry.phone}`.toLowerCase().includes(waitQ)).map(entry => (
                      <tr key={entry.id} className="group transition-colors hover:bg-slate-50">
                        <td className="px-6 py-5"><p className="font-bold text-slate-700">{entry.name}</p><p className="font-mono text-xs font-bold text-slate-400">{entry.email}</p></td>
                        <td className="px-6 py-5"><Badge tone="info" label="PENDING_VET" size="sm" /></td>
                        <td className="px-6 py-5 font-mono text-xs font-bold text-slate-400">{formatDateTime(entry.createdAt)}</td>
                        <td className="px-6 py-5 text-right"><Button variant="ghost" size="sm" className="text-xs font-bold text-rose-500 hover:bg-rose-50" onClick={() => deleteWaitlist(entry.id)}>Purge Record</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AppCard>
        )}

        {tab === "users" && (
          <AppCard title="Vault" subtitle="Authentication and privilege control">
            <div className="space-y-4">
              <div className="relative">
                <input {...usersSearch.register("q")} placeholder="Filter identities..." className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-10 text-sm outline-none focus:border-blue-500" />
                <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-300" />
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-400"><tr><th className="px-6 py-4">Identity</th><th className="px-6 py-4">Privilege</th><th className="px-6 py-4">Status</th><th className="px-6 py-4 text-right">Management</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {state.users.filter(user => `${user.name} ${user.email}`.toLowerCase().includes(userQ)).map(user => (
                      <tr key={user.id} className="group transition-colors hover:bg-slate-50">
                        <td className="px-6 py-5"><p className="font-bold text-slate-700">{user.name}</p><p className="font-mono text-xs font-bold text-slate-400">{user.email}</p></td>
                        <td className="px-6 py-5"><Badge tone="neutral" label={user.role.toUpperCase()} size="sm" /></td>
                        <td className="px-6 py-5"><Badge tone={user.status === "active" ? "success" : "danger"} label={user.status.toUpperCase()} size="sm" /></td>
                        <td className="flex justify-end gap-2 px-6 py-5 text-right">
                          <Button variant="outline" size="sm" className="h-8 bg-white text-xs font-bold" onClick={() => toggleUserStatus(user.id)}>{user.status === "active" ? "Suspend" : "Restore"}</Button>
                          <Button variant="ghost" size="sm" className="h-8 text-xs font-bold text-rose-500 hover:bg-rose-50" onClick={() => deleteUser(user.id)}>Deprovision</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AppCard>
        )}

        {tab === "audit" && (
          <AppCard title="Audit Trail" subtitle="Secure interaction logging" action={<Button variant="outline" size="sm" className="text-xs font-semibold" onClick={() => downloadCsv("audit-log.csv", [["Date", "User", "Prompt", "Response", "IP"], ...state.auditEvents.map(event => [event.occurredAt, event.username, event.prompt, event.responseSnippet, event.ipAddress])])}><Download className="mr-2 h-3.5 w-3.5" /> Export Audit</Button>}>
            <div className="space-y-4">
              <div className="relative">
                <input {...auditSearch.register("q")} placeholder="Filter audit stream..." className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-10 text-sm outline-none focus:border-blue-500" />
                <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-300" />
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-400"><tr><th className="px-6 py-4">Timestamp</th><th className="px-6 py-4">Initiator</th><th className="px-6 py-4">Prompt Snippet</th><th className="px-6 py-4 text-right">IP Identity</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {state.auditEvents.filter(event => `${event.username} ${event.prompt} ${event.responseSnippet}`.toLowerCase().includes(auditQ)).map(event => (
                      <tr key={event.id} className="group transition-colors hover:bg-slate-50">
                        <td className="px-6 py-5 font-mono text-xs font-bold text-slate-400">{formatDateTime(event.occurredAt)}</td>
                        <td className="px-6 py-5 font-bold text-slate-700">{event.username}</td>
                        <td className="max-w-md truncate px-6 py-5 text-xs font-medium italic text-slate-500">"{event.prompt}"</td>
                        <td className="px-6 py-5 text-right font-mono text-xs font-bold text-slate-400">{event.ipAddress}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AppCard>
        )}
      </div>
    </div>
  );
};
